import React, { useState } from 'react';
import { Upload, FileImage, Download, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const PdfImageConverter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState('png');
  const [isConverting, setIsConverting] = useState(false);
  const [convertedFile, setConvertedFile] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        type: file.type,
      });
      setConvertedFile(null);
    }
  };

  const handleConvert = () => {
    setIsConverting(true);
    // Simulate conversion
    setTimeout(() => {
      setConvertedFile({
        name: selectedFile.name.replace(/\.[^/.]+$/, '') + '.' + outputFormat,
        format: outputFormat.toUpperCase(),
      });
      setIsConverting(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
          <FileImage size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">PDF & Image Converter</h1>
          <p className="text-gray-400">Convert between PDF and image formats</p>
        </div>
      </div>

      <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
          {/* Upload Section */}
          <div className="space-y-4">
            <h2 className="text-white font-semibold">1. Upload File</h2>
            {selectedFile ? (
              <div className="bg-[#12121a] rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-3">
                  <FileImage size={32} className="text-purple-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{selectedFile.name}</p>
                    <p className="text-gray-500 text-sm">{selectedFile.size}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-3 border-gray-700 text-gray-300 hover:bg-gray-800"
                  onClick={() => setSelectedFile(null)}
                >
                  Remove File
                </Button>
              </div>
            ) : (
              <label className="block">
                <div className="bg-[#12121a] rounded-lg border-2 border-dashed border-gray-700 p-8 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                  <Upload size={32} className="text-gray-600 mb-3" />
                  <p className="text-gray-400 text-sm">Click to upload</p>
                  <p className="text-gray-500 text-xs mt-1">PDF, PNG, JPG</p>
                </div>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Format Selection */}
          <div className="space-y-4">
            <h2 className="text-white font-semibold">2. Select Output Format</h2>
            <Select value={outputFormat} onValueChange={setOutputFormat}>
              <SelectTrigger className="bg-[#12121a] border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-gray-700">
                <SelectItem value="png" className="text-white hover:bg-purple-600/30">PNG</SelectItem>
                <SelectItem value="jpg" className="text-white hover:bg-purple-600/30">JPG</SelectItem>
                <SelectItem value="pdf" className="text-white hover:bg-purple-600/30">PDF</SelectItem>
                <SelectItem value="webp" className="text-white hover:bg-purple-600/30">WebP</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleConvert}
              disabled={!selectedFile || isConverting}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {isConverting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Converting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ArrowRight size={18} />
                  Convert
                </span>
              )}
            </Button>
          </div>

          {/* Download Section */}
          <div className="space-y-4">
            <h2 className="text-white font-semibold">3. Download Result</h2>
            {convertedFile ? (
              <div className="bg-[#12121a] rounded-lg p-4 border border-green-600/50">
                <div className="flex items-center gap-3 mb-3">
                  <FileImage size={32} className="text-green-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{convertedFile.name}</p>
                    <p className="text-green-400 text-sm">{convertedFile.format} Format</p>
                  </div>
                </div>
                <Button className="w-full bg-green-600 hover:bg-green-700">
                  <Download size={18} className="mr-2" />
                  Download
                </Button>
              </div>
            ) : (
              <div className="bg-[#12121a] rounded-lg p-8 border border-gray-700 flex flex-col items-center justify-center">
                <FileImage size={32} className="text-gray-600 mb-3" />
                <p className="text-gray-500 text-sm text-center">Converted file will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfImageConverter;
